import apiClient from './client';

/**
 * Column preference stored in user preferences
 */
export interface ColumnPreference {
  columnId: string;
  visible: boolean;
  order: number;
}

/**
 * API for managing user preferences
 */
export const userPreferencesApi = {
  /**
   * Get all preferences for the current user
   */
  async getAll(): Promise<Record<string, unknown>> {
    return apiClient.get<Record<string, unknown>>('/api/users/me/preferences');
  },

  /**
   * Get a specific preference by key
   * Returns null if preference doesn't exist
   */
  async get<T = unknown>(key: string): Promise<T | null> {
    return apiClient.get<T | null>(`/api/users/me/preferences/${encodeURIComponent(key)}`);
  },

  /**
   * Set a preference value
   */
  async set<T = unknown>(key: string, value: T): Promise<T> {
    return apiClient.put<T>(`/api/users/me/preferences/${encodeURIComponent(key)}`, { value });
  },

  /**
   * Delete a preference
   */
  async delete(key: string): Promise<void> {
    return apiClient.delete<void>(`/api/users/me/preferences/${encodeURIComponent(key)}`);
  },

  // ============================================================================
  // Convenience methods for column preferences
  // ============================================================================

  /**
   * Get column preferences for a specific cohort's participants table
   */
  async getParticipantsColumnPreferences(cohortId: string): Promise<ColumnPreference[] | null> {
    return this.get<ColumnPreference[]>(`participants_columns_${cohortId}`);
  },

  /**
   * Set column preferences for a specific cohort's participants table
   */
  async setParticipantsColumnPreferences(
    cohortId: string,
    preferences: ColumnPreference[],
  ): Promise<ColumnPreference[]> {
    return this.set(`participants_columns_${cohortId}`, preferences);
  },
};

export default userPreferencesApi;
